"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { MessageSquare, Edit2, Trash2, MoreVerticalIcon, Pin, PinOff, ChevronDown, ChevronUp, CreditCard, LogOut, ExternalLink, Sparkles, Trophy } from "lucide-react";
// import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { toast } from "sonner";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { ProductSwitcher } from "@/components/product-switcher";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useChats, useDeleteChat, useUpdateChat, type Chat } from "@/hooks/useChats";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
// import { ProjectSwitcher } from "./project-switcher";

import type { Organization, Project } from "@/lib/types";

// const OrganizationSwitcher = dynamic(
// 	() => import("./organization-switcher").then((m) => m.OrganizationSwitcher),
// 	{ ssr: false },
// );

export interface ChatSidebarHandle {
  scrollToTop: () => void;
}
interface ChatSidebarProps {
  currentChatId?: string;
  onChatSelect?: (chatId: string) => void;
  onNewChat?: () => void;
  className?: string;
  isLoading?: boolean;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  onOrganizationCreated: (organization: Organization) => void;
  projects: Project[];
  selectedProject: Project | null;
  onSelectProject: (project: Project | null) => void;
  onProjectCreated: (project: Project) => void;
}
type ChatHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "chat";
  key: string;
  chat: Chat;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_CHAT = 52;
const HISTORY_COLLAPSED_STORAGE_KEY = "playground-chat-history-collapsed";
interface ChatHistoryRowProps {
  rows: ChatHistoryRow[];
  currentChatId?: string;
  editingId: string | null;
  editTitle: string;
  pendingFocusChatId: string | null;
  isPageLoading: boolean;
  isMobile: boolean;
  onChatSelect?: (chatId: string) => void;
  onEditTitleChange: (value: string) => void;
  onSaveTitle: (chatId: string) => void;
  onCancelEdit: () => void;
  onDeleteChat: (chatId: string) => void;
  onTogglePin: (chat: Chat) => void;
  onStartEdit: (chat: Chat) => void;
  onEditFocused: () => void;
}
function getChatHistoryRowHeight(index: number, {
  rows
}: ChatHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_CHAT;
}
function EditChatTitleInput(t0) {
  const $ = _c(16);
  const {
    chatId,
    value,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== chatId || $[7] !== onSave) {
    t4 = () => onSave(chatId);
    $[6] = chatId;
    $[7] = onSave;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        onCancel();
      }
    };
    $[9] = onCancel;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 text-sm border-none px-1 focus-visible:ring-0 bg-transparent" />;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
type ChatHistoryItemProps = Pick<ChatHistoryRowProps, "currentChatId" | "editingId" | "editTitle" | "pendingFocusChatId" | "isPageLoading" | "isMobile" | "onChatSelect" | "onEditTitleChange" | "onSaveTitle" | "onCancelEdit" | "onDeleteChat" | "onTogglePin" | "onStartEdit" | "onEditFocused"> & {
  chat: Chat;
};
function ChatHistoryItem(t0) {
  const $ = _c(26);
  const {
    chat,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused
  } = t0;
  const isEditing = editingId === chat.id;
  const isActive = currentChatId === chat.id;
  const isActionFocusable = isActive && isMobile ? undefined : -1;
  let t1;
  if ($[0] !== chat.id || $[1] !== chat.messageCount || $[2] !== chat.title || $[3] !== chat.updatedAt || $[4] !== editTitle || $[5] !== isActive || $[6] !== isEditing || $[7] !== isPageLoading || $[8] !== onCancelEdit || $[9] !== onChatSelect || $[10] !== onEditFocused || $[11] !== onEditTitleChange || $[12] !== onSaveTitle || $[13] !== pendingFocusChatId) {
    t1 = isEditing ? <div className="flex h-full w-full items-center rounded-md px-2 pr-8 text-left text-sm ring-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground"><EditChatTitleInput chatId={chat.id} value={editTitle} shouldFocus={pendingFocusChatId === chat.id} onChange={onEditTitleChange} onSave={onSaveTitle} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton isActive={isActive} onClick={() => onChatSelect?.(chat.id)} className={["h-full! w-full justify-start group relative pr-2 !transition-none group-hover/chat-row:pr-[4.5rem]", isActive ? "max-md:pr-[4.5rem]" : ""].join(" ")} type="button" disabled={isPageLoading}><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{chat.title}</div><div className="text-xs text-muted-foreground">{chat.messageCount} messages • {formatDate(chat.updatedAt)}</div></div></SidebarMenuButton>;
    $[0] = chat.id;
    $[1] = chat.messageCount;
    $[2] = chat.title;
    $[3] = chat.updatedAt;
    $[4] = editTitle;
    $[5] = isActive;
    $[6] = isEditing;
    $[7] = isPageLoading;
    $[8] = onCancelEdit;
    $[9] = onChatSelect;
    $[10] = onEditFocused;
    $[11] = onEditTitleChange;
    $[12] = onSaveTitle;
    $[13] = pendingFocusChatId;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== chat || $[16] !== isActionFocusable || $[17] !== isActive || $[18] !== isEditing || $[19] !== onDeleteChat || $[20] !== onStartEdit || $[21] !== onTogglePin) {
    t2 = !isEditing && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><button type="button" aria-label={chat.pinned ? "Unpin chat" : "Pin chat"} title={chat.pinned ? "Unpin chat" : "Pin chat"} tabIndex={isActionFocusable} onClick={e => {
        e.stopPropagation();
        onTogglePin(chat);
      }} className={["flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground outline-hidden transition-opacity hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring", "pointer-events-none opacity-0 group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100", isActive ? "max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}>{chat.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}</button><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" tabIndex={isActionFocusable} onClick={_temp} className={["pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/chat-row:flex group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100", isActive ? "max-md:flex max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-48"><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onTogglePin(chat);
          }} className="flex items-center gap-2">{chat.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}{chat.pinned ? "Unpin" : "Pin"}</DropdownMenuItem><DropdownMenuItem onClick={e_2 => {
            e_2.stopPropagation();
            onStartEdit(chat);
          }} className="flex items-center gap-2"><Edit2 className="h-4 w-4" />Rename</DropdownMenuItem><DropdownMenuItem onClick={e_3 => {
            e_3.stopPropagation();
            onDeleteChat(chat.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[15] = chat;
    $[16] = isActionFocusable;
    $[17] = isActive;
    $[18] = isEditing;
    $[19] = onDeleteChat;
    $[20] = onStartEdit;
    $[21] = onTogglePin;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== t1 || $[24] !== t2) {
    t3 = <div className="relative h-full px-2 pb-1"><div className="group/chat-row relative h-full">{t1}{t2}</div></div>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
}
function _temp(e_0) {
  e_0.stopPropagation();
}
function ChatHistoryRowComponent(t0) {
  const $ = _c(28);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== currentChatId || $[9] !== editTitle || $[10] !== editingId || $[11] !== isMobile || $[12] !== isPageLoading || $[13] !== onCancelEdit || $[14] !== onChatSelect || $[15] !== onDeleteChat || $[16] !== onEditFocused || $[17] !== onEditTitleChange || $[18] !== onSaveTitle || $[19] !== onStartEdit || $[20] !== onTogglePin || $[21] !== pendingFocusChatId || $[22] !== row.chat) {
    t1 = <ChatHistoryItem chat={row.chat} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={onChatSelect} onEditTitleChange={onEditTitleChange} onSaveTitle={onSaveTitle} onCancelEdit={onCancelEdit} onDeleteChat={onDeleteChat} onTogglePin={onTogglePin} onStartEdit={onStartEdit} onEditFocused={onEditFocused} />;
    $[8] = currentChatId;
    $[9] = editTitle;
    $[10] = editingId;
    $[11] = isMobile;
    $[12] = isPageLoading;
    $[13] = onCancelEdit;
    $[14] = onChatSelect;
    $[15] = onDeleteChat;
    $[16] = onEditFocused;
    $[17] = onEditTitleChange;
    $[18] = onSaveTitle;
    $[19] = onStartEdit;
    $[20] = onTogglePin;
    $[21] = pendingFocusChatId;
    $[22] = row.chat;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  let t2;
  if ($[24] !== ariaAttributes || $[25] !== style || $[26] !== t1) {
    t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
    $[24] = ariaAttributes;
    $[25] = style;
    $[26] = t1;
    $[27] = t2;
  } else {
    t2 = $[27];
  }
  return t2;
}
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return "Just now";
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return format(date, "MMM d");
  }
}
function groupChatsByDate(chats: Chat[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as Chat[],
    yesterday: [] as Chat[],
    lastWeek: [] as Chat[],
    older: [] as Chat[]
  };
  chats.forEach(chat => {
    const chatDate = new Date(chat.updatedAt);
    if (chatDate.toDateString() === today.toDateString()) {
      groups.today.push(chat);
    } else if (chatDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(chat);
    } else if (chatDate >= lastWeek) {
      groups.lastWeek.push(chat);
    } else {
      groups.older.push(chat);
    }
  });
  return groups;
}
export const ChatSidebar = function ChatSidebar(t0) {
  const $ = _c(164);
  const {
    ref,
    currentChatId,
    onChatSelect,
    onNewChat,
    className,
    isLoading: t1,
    organizations,
    selectedOrganization,
    onSelectOrganization
  } = t0;
  const isPageLoading = t1 === undefined ? false : t1;
  const listContainerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => ({
      scrollToTop: () => {
        const scrollEl = listContainerRef.current?.firstElementChild as HTMLElement | null;
        scrollEl?.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    });
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  useImperativeHandle(ref, t2);
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile,
    setOpenMobile
  } = useSidebar();
  const showOrganizationSwitcher = pathname === "/" || pathname === "/group";
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  const resolvedOrgId = selectedOrganization?.id ?? organization?.id;
  const {
    data: chatsData,
    isLoading: isChatsLoading
  } = useChats(resolvedOrgId);
  const deleteChat = useDeleteChat();
  const updateChat = useUpdateChat();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [pendingFocusChatId, setPendingFocusChatId] = useState(null);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(_temp2);
  const [historyTransitionReady, setHistoryTransitionReady] = useState(false);
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setHistoryTransitionReady(true);
    };
    t4 = [];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsHistoryCollapsed(_temp3);
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const toggleHistoryCollapsed = t5;
  chatsData?.chats;
  let t6;
  if ($[4] !== chatsData?.chats) {
    t6 = chatsData?.chats ?? [];
    $[4] = chatsData?.chats;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const chats = t6;
  let t7;
  if ($[6] !== posthog || $[7] !== queryClient || $[8] !== router || $[9] !== signOut) {
    t7 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {
        toast.error("Failed to clear last used project cookies");
      }
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://lounge.llmgateway.io/login");
          }
        }
      });
    };
    $[6] = posthog;
    $[7] = queryClient;
    $[8] = router;
    $[9] = signOut;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const logout = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = chat => {
      setEditingId(chat.id);
      setEditTitle(chat.title);
      setPendingFocusChatId(chat.id);
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleEditTitle = t8;
  let t9;
  if ($[12] !== chats || $[13] !== editTitle || $[14] !== updateChat) {
    t9 = chatId => {
      const nextTitle = editTitle.trim();
      const currentTitle = chats.find(chat_0 => chat_0.id === chatId)?.title.trim();
      if (nextTitle && nextTitle !== currentTitle) {
        updateChat.mutate({
          params: {
            path: {
              id: chatId
            }
          },
          body: {
            title: nextTitle
          }
        });
      }
      setEditingId(null);
      setEditTitle("");
      setPendingFocusChatId(null);
    };
    $[12] = chats;
    $[13] = editTitle;
    $[14] = updateChat;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const saveTitle = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setEditingId(null);
      setEditTitle("");
      setPendingFocusChatId(null);
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const cancelEditTitle = t10;
  let t11;
  if ($[17] !== currentChatId || $[18] !== deleteChat || $[19] !== onNewChat) {
    t11 = chatId_0 => {
      deleteChat.mutate({
        params: {
          path: {
            id: chatId_0
          }
        }
      });
      if (currentChatId === chatId_0) {
        onNewChat?.();
      }
    };
    $[17] = currentChatId;
    $[18] = deleteChat;
    $[19] = onNewChat;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handleDeleteChat = t11;
  let t12;
  if ($[21] !== isMobile || $[22] !== onChatSelect || $[23] !== setOpenMobile) {
    t12 = chatId_1 => {
      onChatSelect?.(chatId_1);
      if (isMobile) {
        setOpenMobile(false);
      }
    };
    $[21] = isMobile;
    $[22] = onChatSelect;
    $[23] = setOpenMobile;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const handleChatSelect = t12;
  let t13;
  if ($[25] !== updateChat) {
    t13 = chat_1 => {
      updateChat.mutate({
        params: {
          path: {
            id: chat_1.id
          }
        },
        body: {
          pinned: !chat_1.pinned
        }
      });
    };
    $[25] = updateChat;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const handleTogglePin = t13;
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setPendingFocusChatId(null);
    };
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const onEditFocused = t14;
  let t15;
  if ($[28] !== chats) {
    t15 = [...chats].sort(_temp4);
    $[28] = chats;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const sortedChats = t15;
  let t16;
  if ($[30] !== sortedChats) {
    t16 = sortedChats.filter(_temp5);
    $[30] = sortedChats;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const pinnedChats = t16;
  let t17;
  if ($[32] !== sortedChats) {
    t17 = groupChatsByDate(sortedChats.filter(_temp6));
    $[32] = sortedChats;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const chatGroups = t17;
  let rows;
  if ($[34] !== chatGroups.lastWeek || $[35] !== chatGroups.older || $[36] !== chatGroups.today || $[37] !== chatGroups.yesterday) {
    const groups = [{
      title: "Today",
      chats: chatGroups.today
    }, {
      title: "Yesterday",
      chats: chatGroups.yesterday
    }, {
      title: "Last 7 days",
      chats: chatGroups.lastWeek
    }, {
      title: "Older",
      chats: chatGroups.older
    }];
    rows = [];
    groups.forEach((t18, groupIndex) => {
      const {
        title,
        chats: groupedChats
      } = t18;
      if (groupedChats.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedChats.forEach(chat_4 => {
        rows.push({
          type: "chat",
          key: `chat-${chat_4.id}`,
          chat: chat_4
        });
      });
      const hasNextGroupWithChats = groups.slice(groupIndex + 1).some(_temp7);
      if (hasNextGroupWithChats) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[34] = chatGroups.lastWeek;
    $[35] = chatGroups.older;
    $[36] = chatGroups.today;
    $[37] = chatGroups.yesterday;
    $[38] = rows;
  } else {
    rows = $[38];
  }
  const historyRows = rows;
  let t18;
  if ($[39] !== currentChatId || $[40] !== editTitle || $[41] !== editingId || $[42] !== handleChatSelect || $[43] !== handleDeleteChat || $[44] !== handleTogglePin || $[45] !== historyRows || $[46] !== isMobile || $[47] !== isPageLoading || $[48] !== pendingFocusChatId || $[49] !== saveTitle) {
    t18 = {
      rows: historyRows,
      currentChatId,
      editingId,
      editTitle,
      pendingFocusChatId,
      isPageLoading,
      isMobile,
      onChatSelect: handleChatSelect,
      onEditTitleChange: setEditTitle,
      onSaveTitle: saveTitle,
      onCancelEdit: cancelEditTitle,
      onDeleteChat: handleDeleteChat,
      onTogglePin: handleTogglePin,
      onStartEdit: handleEditTitle,
      onEditFocused
    };
    $[39] = currentChatId;
    $[40] = editTitle;
    $[41] = editingId;
    $[42] = handleChatSelect;
    $[43] = handleDeleteChat;
    $[44] = handleTogglePin;
    $[45] = historyRows;
    $[46] = isMobile;
    $[47] = isPageLoading;
    $[48] = pendingFocusChatId;
    $[49] = saveTitle;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  const rowProps = t18;
  const isAuthenticated = !!user;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t19;
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <ChatSidebarSkeleton organization={null} />;
      $[51] = t19;
    } else {
      t19 = $[51];
    }
    return t19;
  }
  if (!isAuthenticated) {
    let t19;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <ProductSwitcher />;
      $[52] = t19;
    } else {
      t19 = $[52];
    }
    let t20;
    let t21;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <div className="font-medium mb-2">Sign in required</div>;
      t21 = <p className="text-muted-foreground mb-3">Please sign in to view organizations, projects, and chats.</p>;
      $[53] = t20;
      $[54] = t21;
    } else {
      t20 = $[53];
      t21 = $[54];
    }
    let t22;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[55] = t22;
    } else {
      t22 = $[55];
    }
    let t23;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t19}<div className="w-full rounded-md border p-4 text-sm">{t20}{t21}<div className="flex items-center justify-end gap-2">{t22}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[56] = t23;
    } else {
      t23 = $[56];
    }
    let t24;
    if ($[57] !== className) {
      t24 = <Sidebar className={className}>{t23}</Sidebar>;
      $[57] = className;
      $[58] = t24;
    } else {
      t24 = $[58];
    }
    return t24;
  }
  if (isChatsLoading || isOrgLoading) {
    let t19;
    if ($[59] !== selectedOrganization) {
      t19 = <ChatSidebarSkeleton organization={selectedOrganization} />;
      $[59] = selectedOrganization;
      $[60] = t19;
    } else {
      t19 = $[60];
    }
    return t19;
  }
  const t19 = className + " max-md:hidden";
  let t20;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <SidebarMenuItem><ProductSwitcher /></SidebarMenuItem>;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  const t21 = pathname !== "/";
  let t22;
  if ($[62] !== handleChatSelect || $[63] !== onNewChat || $[64] !== t21) {
    t22 = <SidebarChatSearch disabled={t21} onChatSelect={handleChatSelect} onNewChat={onNewChat} />;
    $[62] = handleChatSelect;
    $[63] = onNewChat;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== isPageLoading || $[67] !== onNewChat) {
    t23 = <SidebarNewAction label="New Chat" onAction={onNewChat} isLoading={isPageLoading} />;
    $[66] = isPageLoading;
    $[67] = onNewChat;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t22 || $[70] !== t23) {
    t24 = <SidebarMenu>{t20}{t22}{t23}</SidebarMenu>;
    $[69] = t22;
    $[70] = t23;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <StudioNav />;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== t24) {
    t26 = <SidebarHeader>{t24}{t25}</SidebarHeader>;
    $[73] = t24;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== onSelectOrganization || $[77] !== organizations || $[78] !== selectedOrganization || $[79] !== showOrganizationSwitcher) {
    t28 = organizations.length > 0 && showOrganizationSwitcher ? <><SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu></> : null;
    $[76] = onSelectOrganization;
    $[77] = organizations;
    $[78] = selectedOrganization;
    $[79] = showOrganizationSwitcher;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  let t29;
  if ($[81] !== t28) {
    t29 = <div>{t27}{t28}</div>;
    $[81] = t28;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  const t30 = !isHistoryCollapsed;
  let t31;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <span>Chats</span>;
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  const t32 = `h-3.5 w-3.5 transition-transform duration-200 ${isHistoryCollapsed ? "-rotate-90" : ""}`;
  let t33;
  if ($[84] !== t32) {
    t33 = <ChevronDown className={t32} />;
    $[84] = t32;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== t30 || $[87] !== t33) {
    t34 = <button type="button" onClick={toggleHistoryCollapsed} aria-expanded={t30} className="flex w-full shrink-0 items-center justify-between px-5 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground group-data-[collapsible=icon]:hidden">{t31}{t33}</button>;
    $[86] = t30;
    $[87] = t33;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  const t35 = `grid min-h-0 flex-1 ${historyTransitionReady ? "transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-[opacity]" : ""} ${isHistoryCollapsed ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100"}`;
  let t36;
  if ($[89] !== isHistoryCollapsed) {
    t36 = isHistoryCollapsed ? {
      inert: true
    } : {};
    $[89] = isHistoryCollapsed;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  let t37;
  if ($[91] !== chats.length || $[92] !== currentChatId || $[93] !== editTitle || $[94] !== editingId || $[95] !== handleChatSelect || $[96] !== handleDeleteChat || $[97] !== handleTogglePin || $[98] !== historyRows || $[99] !== isMobile || $[100] !== isPageLoading || $[101] !== pendingFocusChatId || $[102] !== pinnedChats || $[103] !== rowProps || $[104] !== saveTitle) {
    t37 = chats.length === 0 ? <div className="flex flex-col items-center justify-center py-8 text-center"><MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">No chat history</p><p className="text-xs text-muted-foreground">Start a new conversation to see it here</p></div> : <>{pinnedChats.length > 0 && <div className="shrink-0 max-h-[45%] overflow-y-auto border-b border-sidebar-border/60 pb-1"><div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">Pinned</div>{pinnedChats.map(chat_5 => <div key={chat_5.id} style={{
          height: ROW_HEIGHT_CHAT
        }}><ChatHistoryItem chat={chat_5} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={handleChatSelect} onEditTitleChange={setEditTitle} onSaveTitle={saveTitle} onCancelEdit={cancelEditTitle} onDeleteChat={handleDeleteChat} onTogglePin={handleTogglePin} onStartEdit={handleEditTitle} onEditFocused={onEditFocused} /></div>)}</div>}{historyRows.length > 0 && <List className="min-h-0 w-full flex-1" style={{
        width: "100%"
      }} rowComponent={ChatHistoryRowComponent} rowCount={historyRows.length} rowHeight={getChatHistoryRowHeight} rowProps={rowProps} overscanCount={8} />}</>;
    $[91] = chats.length;
    $[92] = currentChatId;
    $[93] = editTitle;
    $[94] = editingId;
    $[95] = handleChatSelect;
    $[96] = handleDeleteChat;
    $[97] = handleTogglePin;
    $[98] = historyRows;
    $[99] = isMobile;
    $[100] = isPageLoading;
    $[101] = pendingFocusChatId;
    $[102] = pinnedChats;
    $[103] = rowProps;
    $[104] = saveTitle;
    $[105] = t37;
  } else {
    t37 = $[105];
  }
  let t38;
  if ($[106] !== isHistoryCollapsed || $[107] !== t36 || $[108] !== t37) {
    t38 = <div className="flex min-h-0 flex-col overflow-hidden" aria-hidden={isHistoryCollapsed} {...t36}>{t37}</div>;
    $[106] = isHistoryCollapsed;
    $[107] = t36;
    $[108] = t37;
    $[109] = t38;
  } else {
    t38 = $[109];
  }
  let t39;
  if ($[110] !== t35 || $[111] !== t38) {
    t39 = <div className={t35}>{t38}</div>;
    $[110] = t35;
    $[111] = t38;
    $[112] = t39;
  } else {
    t39 = $[112];
  }
  let t40;
  if ($[113] !== isHistoryHidden || $[114] !== t34 || $[115] !== t39) {
    t40 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t34}{t39}</div>;
    $[113] = isHistoryHidden;
    $[114] = t34;
    $[115] = t39;
    $[116] = t40;
  } else {
    t40 = $[116];
  }
  let t41;
  if ($[117] !== t29 || $[118] !== t40) {
    t41 = <SidebarContent className="overflow-hidden pb-2">{t29}{t40}</SidebarContent>;
    $[117] = t29;
    $[118] = t40;
    $[119] = t41;
  } else {
    t41 = $[119];
  }
  let t42;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <SidebarLoungePoints />;
    $[120] = t42;
  } else {
    t42 = $[120];
  }
  const t43 = !selectedOrganization || selectedOrganization.kind === "chat" ? organization : selectedOrganization;
  const t44 = !selectedOrganization || selectedOrganization.kind === "chat";
  let t45;
  if ($[121] !== isOrgLoading || $[122] !== t43 || $[123] !== t44) {
    t45 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t43} isLoading={isOrgLoading} isChatPlanOrg={t44} /></div>;
    $[121] = isOrgLoading;
    $[122] = t43;
    $[123] = t44;
    $[124] = t45;
  } else {
    t45 = $[124];
  }
  const t46 = user?.name ?? "User";
  let t47;
  if ($[125] !== user?.name) {
    t47 = user?.name?.split(" ").map(_temp8).join("").toUpperCase().slice(0, 2) ?? "U";
    $[125] = user?.name;
    $[126] = t47;
  } else {
    t47 = $[126];
  }
  let t48;
  if ($[127] !== t47) {
    t48 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t47}</span></div>;
    $[127] = t47;
    $[128] = t48;
  } else {
    t48 = $[128];
  }
  const t49 = user?.name;
  let t50;
  if ($[129] !== t49) {
    t50 = <span className="truncate font-semibold">{t49}</span>;
    $[129] = t49;
    $[130] = t50;
  } else {
    t50 = $[130];
  }
  const t51 = user?.email;
  let t52;
  if ($[131] !== t51) {
    t52 = <span className="truncate text-xs text-muted-foreground">{t51}</span>;
    $[131] = t51;
    $[132] = t52;
  } else {
    t52 = $[132];
  }
  let t53;
  if ($[133] !== t50 || $[134] !== t52) {
    t53 = <div className="grid flex-1 text-left text-sm leading-tight">{t50}{t52}</div>;
    $[133] = t50;
    $[134] = t52;
    $[135] = t53;
  } else {
    t53 = $[135];
  }
  let t54;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[136] = t54;
  } else {
    t54 = $[136];
  }
  let t55;
  if ($[137] !== t46 || $[138] !== t48 || $[139] !== t53) {
    t55 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t46} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t48}{t53}{t54}</SidebarMenuButton></DropdownMenuTrigger>;
    $[137] = t46;
    $[138] = t48;
    $[139] = t53;
    $[140] = t55;
  } else {
    t55 = $[140];
  }
  let t56;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <DropdownMenuItem asChild={true}><Link href="/profile" prefetch={true}><Sparkles className="mr-2 h-4 w-4" />{"Profile & Points"}</Link></DropdownMenuItem>;
    $[141] = t56;
  } else {
    t56 = $[141];
  }
  let t57;
  if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <DropdownMenuItem asChild={true}><Link href="/leaderboard" prefetch={true}><Trophy className="mr-2 h-4 w-4" />Leaderboard</Link></DropdownMenuItem>;
    $[142] = t57;
  } else {
    t57 = $[142];
  }
  let t58;
  let t59;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t59 = <DropdownMenuSeparator />;
    $[143] = t58;
    $[144] = t59;
  } else {
    t58 = $[143];
    t59 = $[144];
  }
  let t60;
  let t61;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t61 = <DropdownMenuSeparator />;
    $[145] = t60;
    $[146] = t61;
  } else {
    t60 = $[145];
    t61 = $[146];
  }
  let t62;
  if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <span>Theme</span>;
    $[147] = t62;
  } else {
    t62 = $[147];
  }
  let t63;
  let t64;
  if ($[148] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp9}>{t62}<div onClick={_temp10} onKeyDown={_temp11}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t64 = <DropdownMenuSeparator />;
    $[148] = t63;
    $[149] = t64;
  } else {
    t63 = $[148];
    t64 = $[149];
  }
  let t65;
  if ($[150] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <LogOut className="mr-2 h-4 w-4" />;
    $[150] = t65;
  } else {
    t65 = $[150];
  }
  let t66;
  if ($[151] !== logout) {
    t66 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t56}{t57}{t58}{t59}{t60}{t61}{t63}{t64}<DropdownMenuItem onClick={logout}>{t65}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[151] = logout;
    $[152] = t66;
  } else {
    t66 = $[152];
  }
  let t67;
  if ($[153] !== t55 || $[154] !== t66) {
    t67 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t55}{t66}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[153] = t55;
    $[154] = t66;
    $[155] = t67;
  } else {
    t67 = $[155];
  }
  let t68;
  if ($[156] !== t45 || $[157] !== t67) {
    t68 = <SidebarFooter>{t42}{t45}{t67}</SidebarFooter>;
    $[156] = t45;
    $[157] = t67;
    $[158] = t68;
  } else {
    t68 = $[158];
  }
  let t69;
  if ($[159] !== t19 || $[160] !== t26 || $[161] !== t41 || $[162] !== t68) {
    t69 = <Sidebar collapsible="icon" className={t19}>{t26}{t41}{t68}</Sidebar>;
    $[159] = t19;
    $[160] = t26;
    $[161] = t41;
    $[162] = t68;
    $[163] = t69;
  } else {
    t69 = $[163];
  }
  return t69;
};
function _temp2() {
  return typeof document !== "undefined" && document.cookie.split("; ").includes(`${HISTORY_COLLAPSED_STORAGE_KEY}=1`);
}
function _temp3(prev) {
  const next = !prev;
  document.cookie = `${HISTORY_COLLAPSED_STORAGE_KEY}=${next ? "1" : "0"}; path=/; max-age=${31536000}; samesite=lax`;
  return next;
}
function _temp4(a, b) {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}
function _temp5(chat_2) {
  return chat_2.pinned;
}
function _temp6(chat_3) {
  return !chat_3.pinned;
}
function _temp7(group) {
  return group.chats.length > 0;
}
function _temp8(n) {
  return n[0];
}
function _temp9(event) {
  return event.preventDefault();
}
function _temp10(event_0) {
  return event_0.stopPropagation();
}
function _temp11(event_1) {
  return event_1.stopPropagation();
}